'use client';

import { useCallback, useEffect, useRef } from 'react';
import useWebSocketStore, { WebSocketMsg } from '@/lib/hooks/zustand-stores/use-websocket-store';
import { toast } from 'sonner';
import { useLogger } from '@/lib/hooks/useLogger';
import { ReadyState } from './use-managed-websocket';

/**
 * A hook to send WebSocket messages using the Socket.IO connection.
 * 
 * This hook handles both the unified message format and legacy event emission
 * to support the transition from the old to the new approach.
 */
export function useWSMessage() {
  const socket = useWebSocketStore(state => state.socket);
  const connectionStatus = useWebSocketStore(state => state.connectionStatus);
  const getMessagesByType = useWebSocketStore(state => state.getMessagesByType);
  const logger = useLogger();
  const pendingMessages = useRef<{type: string, data: any, timestamp: number}[]>([]);
  
  /**
   * Send a message using the unified message format
   * @param type The event type (e.g., 'chatMessage', 'typingStatus')
   * @param data The data payload
   * @param options Additional options for sending the message
   * @returns true if the message was sent successfully, false otherwise
   */
  const sendMessage = useCallback((
    type: string, 
    data: any, 
    options: {
      /**
       * Whether to show toast messages on errors
       * @default true
       */
      showToasts?: boolean;
      /**
       * Whether to retry sending the message if disconnected
       * @default false
       */
      retryWhenReconnected?: boolean;
      /**
       * Whether to add the message to the local store even if sending fails
       * @default false
       */
      addToStoreOnFail?: boolean;
    } = {}
  ): boolean => {
    const { 
      showToasts = true,
      retryWhenReconnected = false,
      addToStoreOnFail = false
    } = options;

    try {
      // Only for debug/log messages that should not be displayed to end-users
      const isDebugMessageType = type === 'ping' || type === 'heartbeat' || type === 'debug';
      
      // If there's no socket or we're not connected, handle special cases
      if (!socket || connectionStatus !== 'connected') {
        if (retryWhenReconnected) {
          // Save message to send when reconnected
          logger.info(`Queueing message ${type} to send when reconnected`);
          pendingMessages.current.push({
            type,
            data,
            timestamp: Date.now()
          });
        }
        
        if (showToasts && !isDebugMessageType) {
          toast.error('Not connected', { 
            description: 'Message could not be sent because the connection is offline.'
          });
        }
        
        return false;
      }
      
      // Create message object with the standardized format
      const message = { type, data, timestamp: Date.now() };
      
      // In the new architecture, all messages go through the 'message' event
      socket.emit('message', message);
      
      // Also add to our message store
      useWebSocketStore.getState().addMessage(message);
      
      return true;
    } catch (error) {
      logger.error('Error sending WebSocket message:', error);
      
      if (showToasts) {
        toast.error('Failed to send message', {
          description: 'Please try again'
        });
      }
      
      if (addToStoreOnFail) {
        useWebSocketStore.getState().addMessage({
          type,
          data,
          timestamp: Date.now()
        });
      }
      
      return false;
    }
  }, [socket, connectionStatus, logger]);
  
  /**
   * Legacy method to emit a specific event
   * @param event The event name to emit
   * @param data The data payload
   * @param options Additional options for sending the message
   * @returns true if the message was sent successfully, false otherwise
   */
  const emitEvent = useCallback((
    event: string, 
    data: any,
    options: {
      showToasts?: boolean;
      retryWhenReconnected?: boolean;
    } = {}
  ): boolean => {
    const { 
      showToasts = true,
      retryWhenReconnected = false
    } = options;
    
    if (connectionStatus !== 'connected' || !socket) {
      if (showToasts) {
        toast.error('Not connected to WebSocket server', {
          description: 'Please try again when connection is restored'
        });
      }
      
      // Optionally queue for later sending (in new message format)
      if (retryWhenReconnected) {
        pendingMessages.current.push({
          type: event,
          data,
          timestamp: Date.now()
        });
      }
      
      return false;
    }
    
    try {
      // Legacy approach uses specific event names
      socket.emit(event, data);
      
      // Also add to our message store for unified handling
      useWebSocketStore.getState().addMessage({
        type: event,
        data,
        timestamp: Date.now()
      });
      
      return true;
    } catch (error) {
      logger.error(`Error emitting ${event} event:`, error);
      
      if (showToasts) {
        toast.error(`Failed to emit ${event} event`, {
          description: 'Please try again'
        });
      }
      
      return false;
    }
  }, [socket, connectionStatus, logger]);
  
  // Retry sending pending messages when connection is restored
  useEffect(() => {
    if (connectionStatus === 'connected' && socket && pendingMessages.current.length > 0) {
      logger.info(`Connection restored. Sending ${pendingMessages.current.length} pending messages`);
      
      // Use a copy of the queue to avoid issues with new messages being added
      const messagesToSend = [...pendingMessages.current];
      pendingMessages.current = [];
      
      // Send each pending message
      messagesToSend.forEach(msg => {
        try {
          // Send using the unified message format
          socket.emit('message', { type: msg.type, data: msg.data });
          
          useWebSocketStore.getState().addMessage({
            type: msg.type,
            data: msg.data,
            timestamp: Date.now() // Use current time, not queued time
          });
        } catch (err) {
          logger.error('Failed to send queued message:', err);
        }
      });
    }
  }, [connectionStatus, socket, logger]);
  
  /**
   * Get all messages of a specific type
   * @param type The message type to filter by
   * @returns Array of messages with the specified type
   */
  const getMessages = useCallback((type: string): WebSocketMsg[] => {
    return getMessagesByType(type);
  }, [getMessagesByType]);
  
  /**
   * Get the latest message of a specific type
   * @param type The message type to get
   * @returns The latest message with the specified type, or undefined if none found
   */
  const getLatestMessage = useCallback((type: string): WebSocketMsg | undefined => {
    const messages = getMessagesByType(type);
    return messages.length > 0 ? messages[messages.length - 1] : undefined;
  }, [getMessagesByType]);
  
  return {
    // New approach
    sendMessage,
    // Legacy approach
    emitEvent,
    // Helpers for accessing messages
    getMessages,
    getLatestMessage,
    // Connection info
    isConnected: connectionStatus === 'connected' && !!socket,
    connectionStatus,
    hasPendingMessages: pendingMessages.current.length > 0,
    pendingMessageCount: pendingMessages.current.length
  };
}

// Add default export that refers to the named export
export default useWSMessage; 